import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, Monitor, Building, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";

export default function Contact() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ type: "enquiry", name: "", email: "", service_area: "", summary: "", consent: false, company: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/public/settings").then((r) => setSettings(r.data || {})).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please accept the privacy notice."); return; }
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email) || !form.summary.trim()) {
      toast.error("Please complete the required fields."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/enquiries", form);
      toast.success("Thank you — your message has been sent.");
      setForm({ type: "enquiry", name: "", email: "", service_area: "", summary: "", consent: false, company: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Seo title="Contact ApexPath | Get in Touch"
        description="Speak to ApexPath for clear, practical and confidential UK immigration advice." />
      <PageHero
        crumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
        eyebrow="Get in Touch"
        title="We're Here to" highlight="Help"
        image="/images/consult.jpg"
        subtitle="Speak to our team for clear, practical and confidential immigration advice." />

      <section className="bg-white py-16">
        <div className="container-x grid grid-cols-1 gap-10 lg:grid-cols-2">
          {/* Details */}
          <div>
            <h2 className="font-serif text-2xl font-semibold text-navy">Contact Details</h2>
            <ul className="mt-6 space-y-4 text-sm">
              {settings.phone && <li className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/10 text-gold"><Phone size={18} /></span><a href={`tel:${settings.phone}`} className="font-medium text-navy hover:text-gold">{settings.phone}</a></li>}
              {settings.whatsapp && <li className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/10 text-gold"><MessageCircle size={18} /></span><span className="font-medium text-navy">WhatsApp: {settings.whatsapp}</span></li>}
              {settings.email && <li className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/10 text-gold"><Mail size={18} /></span><a href={`mailto:${settings.email}`} className="font-medium text-navy hover:text-gold">{settings.email}</a></li>}
              {settings.office && <li className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/10 text-gold"><MapPin size={18} /></span><span className="font-medium text-navy">{settings.office}</span></li>}
              {settings.hours && <li className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/10 text-gold"><Clock size={18} /></span><span className="font-medium text-navy">{settings.hours}</span></li>}
            </ul>

            <div className="mt-8 rounded-xl bg-cream p-6">
              <h3 className="font-serif text-lg font-semibold text-navy">Consultation options</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><Building size={16} className="text-gold" /> In-person at our office</li>
                <li className="flex items-center gap-2"><Monitor size={16} className="text-gold" /> Online video consultation</li>
                <li className="flex items-center gap-2"><Phone size={16} className="text-gold" /> Phone consultation</li>
              </ul>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <h2 className="font-serif text-2xl font-semibold text-navy">Send Us a Message</h2>
            <div className="mt-5 space-y-4">
              <div>
                <Label htmlFor="c-name">Full name</Label>
                <Input id="c-name" data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="c-email">Email address</Label>
                <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="c-area">How can we help?</Label>
                <Input id="c-area" data-testid="contact-area" value={form.service_area} onChange={(e) => set("service_area", e.target.value)} className="mt-1.5" placeholder="e.g. Spouse visa, Student visa…" />
              </div>
              <div>
                <Label htmlFor="c-msg">Your message</Label>
                <Textarea id="c-msg" data-testid="contact-message" value={form.summary} onChange={(e) => set("summary", e.target.value)} className="mt-1.5 min-h-[120px]" />
              </div>
              <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
                <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="contact-consent" className="mt-0.5" />
                <span>I agree to the <a href="/legal/privacy" className="text-gold underline">privacy notice</a>.</span>
              </label>
              <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-gold w-full disabled:opacity-60">
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
